import { supabase } from '@/integrations/supabase/client';
import { BacktestResult } from './backtestingService';
import { Strategy, BacktestSettingsType } from '@/components/trading/types';
import { v4 as uuidv4 } from 'uuid';

export interface SavedBacktest {
  id: string;
  name: string;
  description?: string;
  type: 'backtest' | 'paper' | 'live';
  status: 'running' | 'completed' | 'paused' | 'error';
  strategyId?: string;
  strategyName?: string;
  startDate: string;
  endDate?: string;
  performance: {
    totalReturn: number;
    annualizedReturn: number;
    maxDrawdown: number;
    sharpeRatio: number;
    trades: number;
    winRate: number;
    profitFactor: number;
  };
  settings: BacktestSettingsType;
  createdAt: string;
  updatedAt: string;
  errorMessage?: string;
}

export interface SaveBacktestParams {
  strategyId?: string;
  name: string;
  description?: string;
  type: 'backtest' | 'paper' | 'live';
  status: 'running' | 'completed' | 'paused' | 'error';
  startDate: string;
  endDate?: string;
  settings: BacktestSettingsType;
  results: BacktestResult & { metadata?: any };
  errorMessage?: string;
}

export interface BacktestDataType {
  equityCurve?: Array<{ date: string; value: number }>;
  trades?: Array<any>;
  positions?: Array<any>;
  openPositions?: Array<any>;
  logs?: Array<any>;
  monthlyReturns?: Array<any>;
}

/**
 * Saves a backtest result to the database
 */
export const saveBacktest = async (userId: string, params: SaveBacktestParams): Promise<string | null> => {
  try {
    console.log('Saving backtest results to database');
    console.log('🔍 DEBUG: Backtest save params:', {
      name: params.name,
      type: params.type,
      status: params.status,
      tradesCount: params.results.trades?.length || 0,
      equityCurveCount: params.results.equityCurve?.length || 0,
      openPositionsCount: params.results.openPositions?.length || 0
    });
    
    // Save to backtests table
    const { data: backtest, error } = await supabase
      .from('backtests')
      .insert({
        user_id: userId,
        strategy_id: params.strategyId,
        name: params.name,
        description: params.description,
        type: params.type,
        status: params.status,
        start_date: params.startDate,
        end_date: params.endDate,
        settings: params.settings,
        performance: params.results.performance,
        error_message: params.errorMessage
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error saving backtest:', error);
      return null;
    }

    const backtestId = backtest.id;
    console.log('✅ Backtest saved successfully with ID:', backtestId);

    // Save equity curve data
    if (params.results.equityCurve && params.results.equityCurve.length > 0) {
      console.log('💾 Saving equity curve data:', params.results.equityCurve.length, 'points');
      const { error: equityCurveError } = await supabase
        .from('backtest_data')
        .insert({
          backtest_id: backtestId,
          data_type: 'equity_curve',
          data: params.results.equityCurve
        });

      if (equityCurveError) {
        console.error('❌ Error saving equity curve data:', equityCurveError);
      } else {
        console.log('✅ Equity curve data saved successfully');
      }
    } else {
      console.log('⚠️ No equity curve data to save');
    }

    // Save trades data
    if (params.results.trades && params.results.trades.length > 0) {
      console.log('💾 Saving trades data:', params.results.trades.length, 'trades');
      console.log('🔍 DEBUG: First few trades:', params.results.trades.slice(0, 3));
      
      const { error: tradesError } = await supabase
        .from('backtest_data')
        .insert({
          backtest_id: backtestId,
          data_type: 'trades',
          data: params.results.trades
        });

      if (tradesError) {
        console.error('❌ Error saving trades data:', tradesError);
        console.error('❌ Trades data that failed to save:', JSON.stringify(params.results.trades.slice(0, 2), null, 2));
      } else {
        console.log('✅ Trades data saved successfully');
      }
    } else {
      console.log('⚠️ No trades data to save - trades array:', params.results.trades);
    }

    // Save open positions data
    if (params.results.openPositions && params.results.openPositions.length > 0) {
      console.log('💾 Saving open positions data:', params.results.openPositions.length, 'positions');
      const { error: openPositionsError } = await supabase
        .from('backtest_data')
        .insert({
          backtest_id: backtestId,
          data_type: 'open_positions',
          data: params.results.openPositions
        });

      if (openPositionsError) {
        console.error('❌ Error saving open positions data:', openPositionsError);
      } else {
        console.log('✅ Open positions data saved successfully');
      }
    }

    // Save logs if they exist
    if (params.results.logs && params.results.logs.length > 0) {
      console.log('💾 Saving logs data:', params.results.logs.length, 'log entries');
      const { error: logsError } = await supabase
        .from('backtest_data')
        .insert({
          backtest_id: backtestId,
          data_type: 'logs',
          data: params.results.logs
        });

      if (logsError) {
        console.error('❌ Error saving logs data:', logsError);
      } else {
        console.log('✅ Logs data saved successfully');
      }
    }

    // Save metadata if it exists
    if ((params.results as any).metadata) {
      console.log('💾 Saving metadata:', (params.results as any).metadata);
      const { error: metadataError } = await supabase
        .from('backtest_data')
        .insert({
          backtest_id: backtestId,
          data_type: 'metadata',
          data: (params.results as any).metadata
        });

      if (metadataError) {
        console.error('❌ Error saving metadata:', metadataError);
      } else {
        console.log('✅ Metadata saved successfully');
      }
    }

    console.log('✅ Backtest saved successfully with ID:', backtestId);
    return backtestId;
  } catch (error) {
    console.error('❌ Exception in saveBacktest:', error);
    return null;
  }
};

/**
 * Gets a list of all backtests for a user
 */
export const getBacktestsList = async (userId: string): Promise<SavedBacktest[]> => {
  try {
    const { data, error } = await supabase
      .from('backtests')
      .select(`
        id,
        name,
        description,
        type,
        status,
        strategy_id,
        strategies:strategy_id (name),
        start_date,
        end_date,
        performance,
        settings,
        created_at,
        updated_at,
        error_message
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching backtests:', error);
      return [];
    }

    return (data || []).map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      type: item.type,
      status: item.status,
      strategyId: item.strategy_id,
      strategyName: item.strategies?.name,
      startDate: item.start_date,
      endDate: item.end_date,
      performance: item.performance,
      settings: item.settings,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      errorMessage: item.error_message
    }));
  } catch (error) {
    console.error('Exception in getBacktestsList:', error);
    return [];
  }
};

/**
 * Gets a specific backtest by ID
 */
export const getBacktestById = async (backtestId: string): Promise<{ backtest: SavedBacktest | null, data: BacktestDataType }> => {
  try {
    console.log('🔍 DEBUG: Fetching backtest by ID:', backtestId);
    
    // Get the backtest details
    const { data: backtestData, error: backtestError } = await supabase
      .from('backtests')
      .select(`
        id,
        name,
        description,
        type,
        status,
        strategy_id,
        strategies:strategy_id (name),
        start_date,
        end_date,
        performance,
        settings,
        created_at,
        updated_at,
        error_message
      `)
      .eq('id', backtestId)
      .single();

    if (backtestError) {
      console.error('❌ Error fetching backtest:', backtestError);
      return { backtest: null, data: {} };
    }

    console.log('✅ Backtest details fetched:', {
      id: backtestData.id,
      name: backtestData.name,
      tradesInPerformance: backtestData.performance?.trades || 0
    });

    // Get associated data (equity curve, trades, etc.)
    const { data: backtestDataItems, error: dataError } = await supabase
      .from('backtest_data')
      .select('data_type, data')
      .eq('backtest_id', backtestId);

    if (dataError) {
      console.error('❌ Error fetching backtest data:', dataError);
      return { 
        backtest: formatBacktest(backtestData), 
        data: {} 
      };
    }

    console.log('🔍 DEBUG: Raw backtest data items from database:', backtestDataItems);
    console.log('🔍 DEBUG: Number of data items found:', backtestDataItems?.length || 0);

    // Organize the data by type
    const backtestDetails: BacktestDataType = {};
    backtestDataItems?.forEach(item => {
      console.log(`🔍 DEBUG: Processing data type '${item.data_type}':`, 
        Array.isArray(item.data) ? `${item.data.length} items` : typeof item.data);
      
      switch (item.data_type) {
        case 'equity_curve':
          backtestDetails.equityCurve = item.data;
          console.log('✅ Set equity curve:', backtestDetails.equityCurve?.length || 0, 'points');
          break;
        case 'trades':
          backtestDetails.trades = item.data;
          console.log('✅ Set trades data:', backtestDetails.trades?.length || 0, 'trades');
          if (backtestDetails.trades?.length > 0) {
            console.log('🔍 DEBUG: First trade:', backtestDetails.trades[0]);
          }
          break;
        case 'positions':
          backtestDetails.positions = item.data;
          console.log('✅ Set positions data:', backtestDetails.positions?.length || 0, 'positions');
          break;
        case 'open_positions':
          backtestDetails.openPositions = item.data;
          console.log('✅ Set open positions data:', backtestDetails.openPositions?.length || 0, 'open positions');
          break;
        case 'logs':
          backtestDetails.logs = item.data;
          console.log('✅ Set logs data:', backtestDetails.logs?.length || 0, 'log entries');
          break;
        case 'monthly_returns':
          backtestDetails.monthlyReturns = item.data;
          console.log('✅ Set monthly returns data:', backtestDetails.monthlyReturns?.length || 0, 'monthly returns');
          break;
        case 'metadata':
          (backtestDetails as any).metadata = item.data;
          console.log('✅ Set metadata:', (backtestDetails as any).metadata);
          break;
      }
    });

    console.log('🔍 DEBUG: Final organized backtest data:', {
      equityCurve: backtestDetails.equityCurve?.length || 0,
      trades: backtestDetails.trades?.length || 0,
      positions: backtestDetails.positions?.length || 0,
      openPositions: backtestDetails.openPositions?.length || 0,
      logs: backtestDetails.logs?.length || 0,
      monthlyReturns: backtestDetails.monthlyReturns?.length || 0
    });

    return { 
      backtest: formatBacktest(backtestData), 
      data: backtestDetails 
    };
  } catch (error) {
    console.error('❌ Exception in getBacktestById:', error);
    return { backtest: null, data: {} };
  }
};

/**
 * Updates a backtest's status
 */
export const updateBacktestStatus = async (backtestId: string, status: 'running' | 'completed' | 'paused' | 'error', errorMessage?: string): Promise<boolean> => {
  try {
    const updateData: any = { status };
    if (status === 'completed') {
      updateData.end_date = new Date().toISOString();
    }
    if (errorMessage) {
      updateData.error_message = errorMessage;
    }

    const { error } = await supabase
      .from('backtests')
      .update(updateData)
      .eq('id', backtestId);

    if (error) {
      console.error('Error updating backtest status:', error);
      return false;
    }

    console.log(`Backtest ${backtestId} status updated to ${status}`);
    return true;
  } catch (error) {
    console.error('Exception in updateBacktestStatus:', error);
    return false;
  }
};

/**
 * Deletes a backtest
 */
export const deleteBacktest = async (backtestId: string): Promise<boolean> => {
  try {
    // The cascade delete will remove all related data
    const { error } = await supabase
      .from('backtests')
      .delete()
      .eq('id', backtestId);

    if (error) {
      console.error('Error deleting backtest:', error);
      return false;
    }

    console.log(`Backtest ${backtestId} deleted successfully`);
    return true;
  } catch (error) {
    console.error('Exception in deleteBacktest:', error);
    return false;
  }
};

/**
 * Saves an LLM analysis of a backtest
 */
export const saveBacktestAnalysis = async (backtestId: string, analysisType: string, content: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('backtest_analysis')
      .insert({
        backtest_id: backtestId,
        analysis_type: analysisType,
        content
      });

    if (error) {
      console.error('Error saving backtest analysis:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Exception in saveBacktestAnalysis:', error);
    return false;
  }
};

/**
 * Gets an LLM analysis of a backtest
 */
export const getBacktestAnalysis = async (backtestId: string, analysisType?: string): Promise<{ type: string, content: string }[]> => {
  try {
    let query = supabase
      .from('backtest_analysis')
      .select('analysis_type, content')
      .eq('backtest_id', backtestId);
    
    if (analysisType) {
      query = query.eq('analysis_type', analysisType);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching backtest analysis:', error);
      return [];
    }

    return (data || []).map(item => ({
      type: item.analysis_type,
      content: item.content
    }));
  } catch (error) {
    console.error('Exception in getBacktestAnalysis:', error);
    return [];
  }
};

/**
 * Helper to format a backtest record from the database
 */
const formatBacktest = (data: any): SavedBacktest => {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    type: data.type,
    status: data.status,
    strategyId: data.strategy_id,
    strategyName: data.strategies?.name,
    startDate: data.start_date,
    endDate: data.end_date,
    performance: data.performance,
    settings: data.settings,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    errorMessage: data.error_message
  };
};